<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';

import JsonTreeNode from './json-editor/JsonTreeNode.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Object, Array],
    default: null,
  },
  copyValue: {
    type: String,
    default: null,
  },
  editable: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'view',
    validator: value => ['view', 'edit'].includes(value),
  },
  language: {
    type: String,
    default: 'auto',
    validator: value => ['auto', 'json', 'text'].includes(value),
  },
  title: {
    type: String,
    default: 'JSON',
  },
  height: {
    type: [String, Number],
    default: null,
  },
  maxHeight: {
    type: [String, Number],
    default: 520,
  },
  placeholder: {
    type: String,
    default: 'Digite o conteúdo aqui…',
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:mode',
  'validationChange',
]);

const labels = {
  collapseAll: 'Recolher',
  copy: 'Copiar',
  copied: 'Copiado',
  copyError: 'Falha ao copiar',
  edit: 'Editar',
  editArea: 'Conteúdo editável',
  expandAll: 'Expandir',
  fullscreen: 'Ocupar tela',
  fullscreenArea: 'Visualização ampliada',
  format: 'Formatar',
  invalidJson: 'JSON inválido',
  modeGroup: 'Modo do editor',
  restore: 'Restaurar',
  validJson: 'JSON válido',
  view: 'Visualizar',
  viewArea: 'Conteúdo formatado',
};

let scrollLockCount = 0;
let bodyOverflowBeforeLock = '';

const lockBodyScroll = () => {
  if (typeof document === 'undefined') return;

  if (scrollLockCount === 0) {
    bodyOverflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  scrollLockCount += 1;
};

const unlockBodyScroll = () => {
  if (typeof document === 'undefined' || scrollLockCount === 0) return;

  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    document.body.style.overflow = bodyOverflowBeforeLock;
    bodyOverflowBeforeLock = '';
  }
};

const toCssSize = value => {
  if (value === null || value === undefined || value === '') return undefined;

  return typeof value === 'number' ? `${value}px` : value;
};

const parseJson = value => {
  try {
    return { valid: true, value: JSON.parse(value), error: null };
  } catch (error) {
    return {
      valid: false,
      value: null,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

const looksLikeJson = value => {
  const text = String(value ?? '').trim();
  if (!text) return false;
  if (/^(?:\{|\[|")/.test(text)) return true;
  if (/^(?:true|false|null)$/.test(text)) return true;

  return /^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:e[+-]?\d+)?$/i.test(text);
};

const resolveLanguage = value => {
  if (props.language !== 'auto') return props.language;
  if (typeof value !== 'string') return 'json';

  const parsed = parseJson(value);
  return parsed.valid || looksLikeJson(value) ? 'json' : 'text';
};

const jsonStateForValue = value => {
  if (typeof value === 'string') return parseJson(value);

  try {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) {
      return { valid: false, value: null, error: 'Valor JSON não suportado.' };
    }

    return { valid: true, value: JSON.parse(serialized), error: null };
  } catch (error) {
    return {
      valid: false,
      value: null,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

const textForValue = (value, language) => {
  if (language === 'text') return value === null ? '' : String(value ?? '');

  const state = jsonStateForValue(value);
  if (!state.valid) {
    return typeof value === 'string' ? value : String(value ?? '');
  }

  return JSON.stringify(state.value, null, 2);
};

const tokeniseJson = source => {
  const tokens = [];
  const pattern =
    /"(?:\\.|[^"\\])*"|-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?|\b(?:true|false)\b|\bnull\b/g;
  let cursor = 0;
  let match = pattern.exec(source);

  while (match) {
    if (match.index > cursor) {
      tokens.push({ type: 'plain', value: source.slice(cursor, match.index) });
    }

    const value = match[0];
    let type = 'number';
    if (value.startsWith('"')) {
      const remainder = source.slice(pattern.lastIndex);
      type = /^\s*:/.test(remainder) ? 'key' : 'string';
    } else if (value === 'true' || value === 'false') {
      type = 'boolean';
    } else if (value === 'null') {
      type = 'null';
    }

    tokens.push({ type, value });
    cursor = pattern.lastIndex;
    match = pattern.exec(source);
  }

  if (cursor < source.length) {
    tokens.push({ type: 'plain', value: source.slice(cursor) });
  }

  return tokens.length ? tokens : [{ type: 'plain', value: source }];
};

const localMode = ref(props.mode);
const localValue = ref(props.modelValue);
const resolvedEditorLanguage = ref(resolveLanguage(props.modelValue));
const draftText = ref(
  textForValue(props.modelValue, resolvedEditorLanguage.value)
);
const validationState = ref({
  valid: true,
  error: null,
  value: localValue.value,
  text: draftText.value,
  language: resolvedEditorLanguage.value,
});
const expansionRevision = ref(0);
const expandAllState = ref(true);
const copyStatus = ref('');
const isExpanded = ref(false);
const shell = ref(null);
const highlightLayer = ref(null);
const editorTextarea = ref(null);
const validationId = `json-editor-validation-${Math.random()
  .toString(36)
  .slice(2)}`;

let copyStatusTimer;
let ownsScrollLock = false;
let focusBeforeExpansion = null;
let isUnmounting = false;

const activeMode = computed(() =>
  props.editable && localMode.value === 'edit' ? 'edit' : 'view'
);
const componentLabel = computed(() => props.title || labels.viewArea);
const bodyStyle = computed(() => ({
  height: toCssSize(props.height),
  maxHeight: toCssSize(props.maxHeight),
}));
const viewLanguage = computed(() => resolveLanguage(localValue.value));
const viewState = computed(() => {
  if (viewLanguage.value === 'text') {
    return {
      valid: true,
      value: localValue.value === null ? '' : String(localValue.value ?? ''),
      error: null,
    };
  }

  return jsonStateForValue(localValue.value);
});
const viewIsCollection = computed(
  () =>
    viewLanguage.value === 'json' &&
    viewState.value.valid &&
    viewState.value.value !== null &&
    typeof viewState.value.value === 'object'
);
const viewText = computed(() => {
  if (viewLanguage.value === 'text') return viewState.value.value;
  if (!viewState.value.valid) return String(localValue.value ?? '');

  return JSON.stringify(viewState.value.value, null, 2);
});
const highlightedTokens = computed(() =>
  resolvedEditorLanguage.value === 'json'
    ? tokeniseJson(draftText.value)
    : [{ type: 'plain', value: draftText.value }]
);
const validationMessage = computed(() => {
  if (validationState.value.language !== 'json') return '';
  if (validationState.value.valid) return labels.validJson;

  return `${labels.invalidJson}: ${validationState.value.error}`;
});
const copySource = computed(() => {
  if (props.copyValue !== null) return props.copyValue;
  if (activeMode.value === 'edit') return draftText.value;

  return viewText.value;
});

const emitValidation = state => {
  validationState.value = state;
  emit('validationChange', { ...state });
};

const validateDraft = () => {
  if (resolvedEditorLanguage.value === 'text') {
    return {
      valid: true,
      error: null,
      value: draftText.value,
      text: draftText.value,
      language: 'text',
    };
  }

  if (!draftText.value.trim()) {
    return {
      valid: false,
      error: 'O JSON não pode ficar vazio.',
      value: null,
      text: draftText.value,
      language: 'json',
    };
  }

  const parsed = parseJson(draftText.value);
  return {
    valid: parsed.valid,
    error: parsed.error,
    value: parsed.value,
    text: draftText.value,
    language: 'json',
  };
};

const matchesCurrentDraft = value => {
  if (resolvedEditorLanguage.value === 'text') {
    return String(value ?? '') === draftText.value;
  }

  const current = validateDraft();
  if (!current.valid) return value === draftText.value;

  const incoming = jsonStateForValue(value);
  if (!incoming.valid) return false;

  return JSON.stringify(incoming.value) === JSON.stringify(current.value);
};

const syncFromModel = (value, shouldEmitValidation = true) => {
  localValue.value = value;
  resolvedEditorLanguage.value = resolveLanguage(value);
  draftText.value = textForValue(value, resolvedEditorLanguage.value);
  const validation = validateDraft();

  if (shouldEmitValidation) {
    emitValidation(validation);
  } else {
    validationState.value = validation;
  }
};

const setMode = nextMode => {
  if (!['view', 'edit'].includes(nextMode)) return;
  if (nextMode === 'edit' && !props.editable) return;
  if (
    nextMode === 'view' &&
    activeMode.value === 'edit' &&
    resolvedEditorLanguage.value === 'json' &&
    !validateDraft().valid
  ) {
    emitValidation(validateDraft());
    nextTick(() => editorTextarea.value?.focus());
    return;
  }

  localMode.value = nextMode;
  emit('update:mode', nextMode);
};

const setAllExpanded = expanded => {
  expandAllState.value = expanded;
  expansionRevision.value += 1;
};

const toggleAllExpanded = () => {
  setAllExpanded(!expandAllState.value);
};

const syncEditorScroll = event => {
  if (!highlightLayer.value) return;

  highlightLayer.value.scrollTop = event.target.scrollTop;
  highlightLayer.value.scrollLeft = event.target.scrollLeft;
};

const onEditorInput = event => {
  draftText.value = event.target.value;

  if (
    props.language === 'auto' &&
    resolvedEditorLanguage.value === 'text' &&
    looksLikeJson(draftText.value)
  ) {
    resolvedEditorLanguage.value = 'json';
  }

  const validation = validateDraft();
  emitValidation(validation);

  localValue.value = draftText.value;
  emit('update:modelValue', draftText.value);
};

const formatJson = () => {
  const validation = validateDraft();
  if (!validation.valid || validation.language !== 'json') {
    emitValidation(validation);
    return;
  }

  draftText.value = JSON.stringify(validation.value, null, 2);
  localValue.value = draftText.value;
  const formattedValidation = validateDraft();
  emitValidation(formattedValidation);
  emit('update:modelValue', draftText.value);
};

const fallbackCopy = text => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.readOnly = true;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const copied = document.execCommand('copy');
    if (!copied) throw new Error('Copy command was rejected.');

    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  } finally {
    document.body.removeChild(textarea);
  }
};

const copyText = text => {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  }

  return fallbackCopy(text);
};

const showCopyStatus = status => {
  copyStatus.value = status;
  window.clearTimeout(copyStatusTimer);
  copyStatusTimer = window.setTimeout(() => {
    copyStatus.value = '';
  }, 1600);
};

const copyContent = async () => {
  try {
    await copyText(copySource.value);
    showCopyStatus(labels.copied);
  } catch (error) {
    showCopyStatus(labels.copyError);
  }
};

const handleExpandedKeydown = event => {
  if (!isExpanded.value || event.key !== 'Escape') return;

  event.preventDefault();
  event.stopImmediatePropagation();
  isExpanded.value = false;
};

const toggleExpanded = force => {
  isExpanded.value = typeof force === 'boolean' ? force : !isExpanded.value;
};

const slotBindings = computed(() => ({
  mode: activeMode.value,
  isExpanded: isExpanded.value,
  valid: validationState.value.valid,
  error: validationState.value.error,
  value: validationState.value.value,
  text: draftText.value,
  setMode,
  toggleExpanded,
}));

watch(
  () => props.modelValue,
  value => {
    localValue.value = value;
    if (activeMode.value === 'edit' && matchesCurrentDraft(value)) return;

    syncFromModel(value);
  },
  { deep: true }
);

watch(
  () => props.mode,
  value => {
    localMode.value = value;
  }
);

watch(
  () => props.language,
  () => {
    syncFromModel(localValue.value);
  }
);

watch(isExpanded, expanded => {
  if (expanded) {
    focusBeforeExpansion = document.activeElement;
    lockBodyScroll();
    ownsScrollLock = true;
    window.addEventListener('keydown', handleExpandedKeydown, true);
    nextTick(() => shell.value?.focus());
    return;
  }

  window.removeEventListener('keydown', handleExpandedKeydown, true);
  if (ownsScrollLock) {
    unlockBodyScroll();
    ownsScrollLock = false;
  }

  const focusTarget = focusBeforeExpansion;
  focusBeforeExpansion = null;
  if (!isUnmounting && focusTarget instanceof HTMLElement) {
    nextTick(() => {
      if (focusTarget.isConnected) focusTarget.focus();
    });
  }
});

onMounted(() => {
  emitValidation(validateDraft());
});

onBeforeUnmount(() => {
  isUnmounting = true;
  window.clearTimeout(copyStatusTimer);
  window.removeEventListener('keydown', handleExpandedKeydown, true);
  if (ownsScrollLock) unlockBodyScroll();
  ownsScrollLock = false;
});
</script>

<template>
  <Teleport to="body" :disabled="!isExpanded">
    <section
      ref="shell"
      class="json-editor"
      :class="{ 'json-editor--expanded': isExpanded }"
      :aria-label="componentLabel"
      :aria-roledescription="isExpanded ? labels.fullscreenArea : undefined"
      tabindex="-1"
    >
      <header class="json-editor__toolbar">
        <div class="json-editor__heading">
          <strong class="json-editor__title">{{ title }}</strong>
          <span class="json-editor__copy-status" aria-live="polite">
            {{ copyStatus }}
          </span>
        </div>

        <div class="json-editor__toolbar-controls">
          <div
            v-if="editable"
            class="json-editor__mode-switch"
            role="group"
            :aria-label="labels.modeGroup"
          >
            <button
              type="button"
              class="json-editor__mode-option"
              :class="{
                'json-editor__mode-option--active': activeMode === 'view',
              }"
              :aria-pressed="activeMode === 'view'"
              data-testid="json-editor-mode-view"
              @click="setMode('view')"
            >
              {{ labels.view }}
            </button>
            <button
              type="button"
              class="json-editor__mode-option"
              :class="{
                'json-editor__mode-option--active': activeMode === 'edit',
              }"
              :aria-pressed="activeMode === 'edit'"
              data-testid="json-editor-mode-edit"
              @click="setMode('edit')"
            >
              {{ labels.edit }}
            </button>
          </div>

          <button
            v-if="activeMode === 'edit' && resolvedEditorLanguage === 'json'"
            type="button"
            class="json-editor__action"
            data-testid="json-editor-format"
            :disabled="!validationState.valid"
            @click="formatJson"
          >
            {{ labels.format }}
          </button>

          <button
            v-if="activeMode === 'view' && viewIsCollection"
            type="button"
            class="json-editor__action"
            data-testid="json-editor-toggle-all"
            :aria-expanded="expandAllState"
            @click="toggleAllExpanded"
          >
            {{ expandAllState ? labels.collapseAll : labels.expandAll }}
          </button>

          <slot name="toolbar-actions" v-bind="slotBindings" />

          <button
            type="button"
            class="json-editor__action json-editor__action--primary"
            data-testid="json-editor-copy"
            @click="copyContent"
          >
            {{ labels.copy }}
          </button>
          <button
            type="button"
            class="json-editor__action json-editor__action--icon"
            data-testid="json-editor-fullscreen"
            :aria-pressed="isExpanded"
            :aria-label="isExpanded ? labels.restore : labels.fullscreen"
            :title="isExpanded ? labels.restore : labels.fullscreen"
            @click="toggleExpanded()"
          >
            <span
              class="json-editor__fullscreen-icon"
              :class="
                isExpanded ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'
              "
              aria-hidden="true"
            />
          </button>
        </div>
      </header>

      <div
        v-if="activeMode === 'view'"
        class="json-editor__body json-editor__body--view"
        :style="bodyStyle"
        role="region"
        :aria-label="labels.viewArea"
      >
        <JsonTreeNode
          v-if="viewLanguage === 'json' && viewState.valid"
          :value="viewState.value"
          :expansion-revision="expansionRevision"
          :expand-all="expandAllState"
        />
        <pre v-else class="json-editor__text">{{ viewText }}</pre>
      </div>

      <div
        v-else
        class="json-editor__body json-editor__body--edit"
        :style="bodyStyle"
      >
        <div class="json-editor__editing-area">
          <pre
            v-if="resolvedEditorLanguage === 'json'"
            ref="highlightLayer"
            class="json-editor__highlight"
            aria-hidden="true"
          ><code><span
            v-for="(token, index) in highlightedTokens"
            :key="`${index}-${token.type}`"
            :class="`json-editor__syntax--${token.type}`"
          >{{ token.value }}</span></code></pre>
          <textarea
            ref="editorTextarea"
            class="json-editor__textarea"
            :class="{
              'json-editor__textarea--highlighted':
                resolvedEditorLanguage === 'json',
            }"
            :value="draftText"
            :placeholder="placeholder"
            :aria-label="labels.editArea"
            :aria-invalid="!validationState.valid"
            :aria-describedby="
              validationState.language === 'json' ? validationId : undefined
            "
            wrap="off"
            spellcheck="false"
            @input="onEditorInput"
            @scroll="syncEditorScroll"
          />
        </div>

        <footer class="json-editor__edit-footer">
          <p
            v-if="validationState.language === 'json'"
            :id="validationId"
            class="json-editor__validation"
            :class="{
              'json-editor__validation--error': !validationState.valid,
            }"
            :role="validationState.valid ? 'status' : 'alert'"
          >
            {{ validationMessage }}
          </p>
          <span v-else />

          <div class="json-editor__edit-actions">
            <slot name="edit-actions" v-bind="slotBindings" />
          </div>
        </footer>
      </div>
    </section>
  </Teleport>
</template>

<style scoped>
.json-editor {
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 0.5rem;
  box-sizing: border-box;
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 4%);
  color: #cbd5e1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.json-editor:focus {
  outline: none;
}

.json-editor--expanded {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  height: 100vh;
  height: 100dvh;
  inset: 0;
  max-height: none;
  position: fixed;
  width: 100vw;
  z-index: 2147483000;
}

.json-editor__toolbar {
  align-items: center;
  background: #111827;
  border-bottom: 1px solid #1f2937;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.625rem 0.75rem;
}

.json-editor__heading,
.json-editor__toolbar-controls,
.json-editor__edit-actions {
  align-items: center;
  display: flex;
}

.json-editor__heading {
  flex-wrap: wrap;
  gap: 0.5rem;
  min-width: 0;
}

.json-editor__title {
  color: #e5e7eb;
  font-size: 0.75rem;
}

.json-editor__copy-status {
  color: #86efac;
  font-size: 0.75rem;
  font-weight: 700;
  min-height: 1em;
}

.json-editor__toolbar-controls {
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
  min-width: 0;
}

.json-editor__mode-switch {
  background: #020617;
  border: 1px solid #475569;
  border-radius: 0.5rem;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  padding: 0.125rem;
}

.json-editor__mode-option {
  background: transparent;
  border: 0;
  border-radius: 0.375rem;
  color: #94a3b8;
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.45rem 0.65rem;
  transition:
    background-color 120ms ease,
    box-shadow 120ms ease,
    color 120ms ease;
  white-space: nowrap;
}

.json-editor__mode-option:hover,
.json-editor__mode-option:focus-visible {
  color: #f8fafc;
  outline: none;
}

.json-editor__mode-option--active {
  background: #0f766e;
  box-shadow:
    0 1px 2px rgb(0 0 0 / 35%),
    inset 0 0 0 1px #14b8a6;
  color: #ffffff;
}

.json-editor__action {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 0.375rem;
  color: #cbd5e1;
  cursor: pointer;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.45rem 0.55rem;
  white-space: nowrap;
}

.json-editor__action:hover,
.json-editor__action:focus-visible {
  background: #334155;
  border-color: #64748b;
  color: #ffffff;
  outline: none;
}

.json-editor__action--primary {
  background: #0f766e;
  border-color: #0d9488;
  color: #ecfeff;
}

.json-editor__action--primary:hover,
.json-editor__action--primary:focus-visible {
  background: #0d9488;
  border-color: #2dd4bf;
}

.json-editor__action:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.json-editor__action--icon {
  align-items: center;
  display: inline-flex;
  height: 1.875rem;
  justify-content: center;
  padding: 0.375rem;
  width: 1.875rem;
}

.json-editor__fullscreen-icon {
  display: inline-block;
  height: 1rem;
  width: 1rem;
}

.json-editor__body {
  background: #0f172a;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
  font-size: 0.75rem;
  line-height: 1.6;
  min-height: 0;
  tab-size: 2;
}

.json-editor__body--view {
  overflow: auto;
  padding: 0.75rem 0;
  scrollbar-gutter: stable;
}

.json-editor__body--edit {
  display: flex;
  flex-direction: column;
  height: 20rem;
  overflow: hidden;
}

.json-editor--expanded .json-editor__body {
  flex: 1 1 auto;
  height: auto !important;
  max-height: none !important;
}

.json-editor__text {
  color: #e2e8f0;
  margin: 0;
  overflow-wrap: anywhere;
  padding: 0 0.875rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.json-editor__editing-area {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
}

.json-editor__highlight,
.json-editor__textarea {
  border: 0;
  box-sizing: border-box;
  font: inherit;
  height: 100%;
  inset: 0;
  line-height: inherit;
  margin: 0;
  overflow: auto;
  padding: 0.875rem;
  position: absolute;
  resize: none;
  tab-size: inherit;
  white-space: pre;
  width: 100%;
  word-spacing: normal;
}

.json-editor__highlight {
  background: transparent;
  color: #cbd5e1;
  pointer-events: none;
  scrollbar-width: none;
}

.json-editor__highlight::-webkit-scrollbar {
  display: none;
}

.json-editor__highlight code {
  font: inherit;
  line-height: inherit;
  white-space: inherit;
}

.json-editor__textarea {
  background: transparent;
  caret-color: #f8fafc;
  color: #e2e8f0;
  outline: none;
}

.json-editor__textarea--highlighted {
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.json-editor__textarea::placeholder {
  color: #64748b;
  -webkit-text-fill-color: #64748b;
}

.json-editor__textarea:focus-visible {
  box-shadow: inset 0 0 0 2px #0d9488;
}

.json-editor__textarea::selection {
  background: rgb(59 130 246 / 38%);
}

.json-editor__syntax--key {
  color: #7dd3fc;
}

.json-editor__syntax--string {
  color: #86efac;
}

.json-editor__syntax--number {
  color: #fbbf24;
}

.json-editor__syntax--boolean {
  color: #c4b5fd;
}

.json-editor__syntax--null {
  color: #94a3b8;
  font-style: italic;
}

.json-editor__syntax--plain {
  color: #cbd5e1;
}

.json-editor__edit-footer {
  align-items: center;
  background: #111827;
  border-top: 1px solid #1f2937;
  display: flex;
  flex: 0 0 auto;
  gap: 0.75rem;
  justify-content: space-between;
  min-height: 2.5rem;
  padding: 0.5rem 0.75rem;
}

.json-editor__validation {
  color: #86efac;
  font-size: 0.75rem;
  font-weight: 700;
  margin: 0;
}

.json-editor__validation--error {
  color: #fca5a5;
}

@media (max-width: 640px) {
  .json-editor__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .json-editor__toolbar-controls {
    justify-content: flex-start;
    width: 100%;
  }

  .json-editor__edit-footer {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
